import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiError } from "@/lib/api-error";
import { AUTH_ERROR_OPTIONS } from "@/lib/auth-error-options";
import { resetPassword } from "@/services/api/auth";

export function TenantResetPasswordPage() {
  const t = useTranslations("auth");
  const tRoot = useTranslations();
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  // H2: the link in the welcome email for an account created without a password.
  const welcome = searchParams.get("welcome") === "1";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  // MT-1 Plan 2 S17 (FR-3 e): an invite link that no longer works points at Forgot password.
  const [inviteInvalid, setInviteInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      setInviteInvalid(welcome);
      setError(t(welcome ? "welcomeLinkInvalid" : "resetLinkInvalid"));
      return;
    }

    setLoading(true);
    setError(null);
    setInviteInvalid(false);

    try {
      await resetPassword({ token, password, tenantSlug });
      navigate(`/t/${tenantSlug}/login?reset=1`);
    } catch (err) {
      if (welcome && readApiError(err)?.code === "INVALID_TOKEN") {
        setInviteInvalid(true);
        setError(t("welcomeLinkInvalid"));
        return;
      }
      setError(apiErrorText(err, tRoot, "auth.requestFailed", AUTH_ERROR_OPTIONS));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-2 text-2xl font-semibold">
        {t(welcome ? "setPasswordTitle" : "resetPasswordTitle")}
      </h1>
      {welcome && (
        <p className="mb-2 text-sm" data-testid="reset-password-welcome">
          {t("setPasswordIntro")}
        </p>
      )}
      <p className="mb-6 text-sm text-[var(--text-muted)]">
        {t("tenantLabel", { tenant: tenantSlug })}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium">
            {t("newPassword")}
          </label>
          <input
            id="password"
            data-testid="reset-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
          <p className="mt-1 text-xs text-[var(--text-muted)]">{t("passwordHintLong")}</p>
        </div>
        {error && (
          <p className="text-sm text-[var(--danger)]" data-testid="reset-password-error">
            {error}
          </p>
        )}
        {inviteInvalid && (
          <p className="text-sm">
            <Link
              to={`/t/${tenantSlug}/forgot-password`}
              data-testid="reset-password-forgot-link"
              className="text-[var(--text-heading)] underline"
            >
              {t("forgotPassword")}
            </Link>
          </p>
        )}
        <button
          type="submit"
          data-testid="reset-password-submit"
          disabled={loading}
          className="w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
        >
          {loading ? t("updating") : t(welcome ? "savePassword" : "updatePassword")}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to={`/t/${tenantSlug}/login`} className="text-[var(--text-heading)] underline">
          {t("backToSignIn")}
        </Link>
      </p>
    </main>
  );
}
